import { useEffect } from 'react';
import {
  Navigate,
  Outlet,
  useParams,
  useNavigate,
  useLocation,
} from 'react-router';
import { useFund } from '@/hooks/react-query/useFunds';
import { PageSkeleton } from '@/components/ui/DataLoader';
import { Button } from '@/components/ui/button';
import { FundHeader } from './FundHeader';
import type { FundLayoutContext } from './useFundLayoutContext';

interface FundPageLayoutProps {
  // Starts the page chunk download alongside the fund fetch instead of after it.
  preload?: () => Promise<unknown>;
}

export function FundPageLayout({ preload }: FundPageLayoutProps) {
  const { fundId } = useParams<{ fundId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: fund, isLoading } = useFund(fundId);

  const hideEditButton = location.pathname.includes('/email-compose');

  useEffect(() => {
    void preload?.();
  }, [preload]);

  if (!fundId) {
    return <Navigate to="/app/funds" replace />;
  }

  if (isLoading) {
    return (
      <div className="p-6" data-testid="loading">
        <PageSkeleton showCards />
      </div>
    );
  }

  if (!fund) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-foreground">
            Fund not found
          </h2>
          <p className="text-muted-foreground mt-2">
            The requested fund could not be found.
          </p>
          <Button onClick={() => navigate('/app/funds')} className="mt-4">
            Back to Funds
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full p-6 flex flex-col gap-3">
      <FundHeader fund={fund} fundId={fundId} hideEditButton={hideEditButton} />
      <Outlet context={{ fund, fundId } satisfies FundLayoutContext} />
    </div>
  );
}
